'use client';
/**
 * Pont entre l'ADRESSE du navigateur et l'état de l'application.
 *
 * ── Pourquoi un pont et non un routeur ──────────────────────────────────────
 *
 * L'écran courant vit dans le store (`view` + sélection). Le porter dans le
 * routeur de Next aurait voulu dire découper l'application en cinquante
 * segments de route et déplacer la moitié de la logique de navigation — pour
 * un résultat identique du point de vue de l'utilisateur. On garde donc l'état
 * là où il est, et on le REFLÈTE dans l'adresse avec l'API History.
 *
 * Le contrat est simple et se lit dans les deux sens :
 *   · l'application navigue  → l'adresse suit   (`pushState`)
 *   · l'adresse change       → l'application suit (`popstate`, et au 1er rendu)
 *
 * ── Le piège de la boucle ───────────────────────────────────────────────────
 *
 * Les deux sens s'appellent l'un l'autre. Sans garde, `popstate` change l'état,
 * l'état pousse une adresse, qui déclenche… La garde est `posePar` : quand
 * c'est l'URL qui commande, on marque le coup et l'effet de synchronisation
 * REMPLACE au lieu d'empiler. Rien de plus subtil, et rien de moins.
 *
 * ── Ce qui n'est PAS adressable, et c'est voulu ─────────────────────────────
 *
 * Les panneaux superposés (`ouvrirPanneau`) ne changent pas l'adresse : ils
 * s'ouvrent PAR-DESSUS un écran qu'on ne quitte pas, et l'adresse doit
 * continuer à désigner la page où l'on se trouve. Un lien vers une fiche
 * ouverte en panneau mènerait le destinataire à un écran dont il ne
 * comprendrait pas le contexte — d'où le bouton « ouvrir en pleine page », qui
 * lui donne une vraie adresse.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useGed, capturerScrolls } from '@/lib/store';
import { ADRESSES, cheminDe, lireChemin, resoudre } from '@/lib/adresse';
import { titreDeLaPage } from '@/lib/titrePage';
import type { GedView } from '@/lib/types';

/** Vrai pendant qu'on applique une adresse : la synchro ne doit pas empiler. */
let posePar: 'url' | null = null;

/** Registre du store où retrouver une fiche, par nom de registre logique. */
type Ctx = Record<string, unknown>;
function registreDe(nom: string, s: Ctx): { ref?: string; code?: string; nom?: string; documentId?: string }[] | null {
  const t = (k: string) => (Array.isArray(s[k]) ? (s[k] as Ctx[]) : null) as never;
  switch (nom) {
    case 'nc': return t('ncs');
    case 'action': return t('actions');
    case 'recl': return t('recls');
    case 'audit': return t('audits');
    case 'rdd': return t('rdds');
    case 'equip': return t('equips');
    case 'doc': return t('docs');
    default: return null;
  }
}

/**
 * Référence LISIBLE de la fiche actuellement ouverte, pour l'écrire dans l'URL.
 *
 * Le store retient un `documentId` ; l'adresse doit porter `BIO24-NC-2026-014`.
 * Quand la conversion échoue (registre pas encore chargé), on rend `null` et
 * l'adresse reste celle de la liste — jamais un identifiant technique de repli,
 * qui finirait collé dans un courriel et ne vaudrait plus rien après une
 * reprise de données.
 */
function referenceOuverte(vue: GedView, s: Ctx): string | null {
  const a = ADRESSES[vue];
  if (!a?.cle) return null;
  const valeur = String(s[a.cle] || '');
  if (!valeur) return null;
  // Une clé DIRECTE porte déjà la référence : rien à convertir.
  if (a.directe) return valeur;
  const reg = a.registre ? registreDe(a.registre, s) : null;
  const trouve = reg?.find((r) => r.documentId === valeur);
  return trouve ? String(trouve.ref || trouve.code || trouve.nom || '') || null : null;
}

export function useAdresse() {
  const etat = useGed();
  const dernierChemin = useRef<string | null>(null);
  /** L'adresse d'arrivée a-t-elle été lue ? Tant que non, on n'écrit RIEN. */
  const lue = useRef(false);

  // ── Sens 0 : LIRE l'adresse d'arrivée, avant toute écriture ───────────────
  //
  // ⚠️ Cet effet doit être déclaré EN PREMIER, et le suivant s'y subordonne.
  // Dans l'autre ordre — c'est l'erreur qui a été faite et vue à l'écran — la
  // synchronisation s'exécute d'abord avec l'état PAR DÉFAUT du store (le
  // tableau de bord), remplace l'adresse par « / », et la lecture qui suit ne
  // trouve plus qu'une racine : tout lien profond menait au tableau de bord,
  // silencieusement. Un lien partagé qui ouvre le mauvais écran sans rien dire
  // est pire qu'un lien cassé.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    posePar = 'url';
    useGed.getState().appliquerAdresse(window.location.pathname);
    dernierChemin.current = window.location.pathname;
    lue.current = true;
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  // ── Sens 1 : l'application navigue, l'adresse suit ────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined' || !lue.current) return;
    /* ⚠️ L'état est relu par `getState()`, PAS pris dans la fermeture du rendu.
       C'est le défaut qui a fait échouer tout lien profond au premier essai :
       l'effet de lecture d'adresse (sens 0) pose l'écran demandé, puis CET
       effet s'exécute dans la MÊME phase avec le `view` du rendu précédent —
       « tableau de bord ». Il remplaçait donc aussitôt « /documents » par
       « / », et l'adresse partagée était perdue avant d'avoir servi.

       Les dépendances ci-dessous ne servent qu'à DÉCLENCHER l'effet ; la
       valeur, elle, se lit toujours au moment où l'on écrit. */
    const s = useGed.getState() as unknown as Ctx;
    /* Un lien est EN COURS DE RÉSOLUTION : l'adresse reste maîtresse et on n'y
       touche pas. Sans cette garde, la synchronisation réécrivait l'adresse
       sans la référence (la fiche n'étant pas encore retrouvée), et la cible
       du lien était perdue avant même que les données n'arrivent — le lien
       ouvrait la liste au lieu de la fiche, sans un mot. */
    if (s.lienEnAttente) return;
    const vue = (s.view as GedView);
    const ref = referenceOuverte(vue, s);
    const chemin = cheminDe(vue, ref);
    if (chemin === window.location.pathname) { dernierChemin.current = chemin; return; }

    /* ⚠️ LA POSITION DE DÉFILEMENT EST ÉCRITE DANS L'ENTRÉE QU'ON QUITTE.
       (28/08/2026 — ce qui manquait pour que « Précédent » vaille les flèches
       de l'application.)

       Elle ne peut pas être écrite en poussant l'entrée SUIVANTE : à ce
       moment-là on est déjà parti, et la position n'est plus celle qu'on veut
       retrouver. On repasse donc sur l'entrée COURANTE (`replaceState`) juste
       avant d'empiler, pour y déposer où l'on en était — page ET zones
       internes, qui sont ce qui manquait le plus : revenir sur une matrice de
       237 lignes remise en haut oblige à rechercher sa ligne. */
    if (posePar === 'url') {
      // L'URL commande (arrivée, « Précédent ») : on REMPLACE. Empiler
      // ajouterait une entrée fantôme et il faudrait deux clics pour revenir
      // en arrière une fois.
      window.history.replaceState({ ...(window.history.state || {}), ged: chemin }, '', chemin);
    } else {
      const etat = window.history.state || {};
      window.history.replaceState(
        { ...etat, scrollY: window.scrollY, scrollsInternes: capturerScrolls() },
        '',
        window.location.pathname,
      );
      window.history.pushState({ ged: chemin }, '', chemin);
    }
    dernierChemin.current = chemin;
    posePar = null;
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [etat.lienEnAttente, etat.view, etat.selectedNC, etat.selectedAction, etat.selectedRecl, etat.selectedAudit,
    etat.selectedRdd, etat.selectedEquip, etat.selectedFourn, etat.selectedCode,
    /* 17/10/2026 — l'examen ouvert au référentiel : sans cette dépendance,
       ouvrir une fiche depuis la liste (même vue, seule la clé change) ne
       réécrivait pas l'adresse, et le lien copié était celui de la liste. */
    etat.selectedRef,
    etat.ncs, etat.actions, etat.recls, etat.audits, etat.rdds, etat.equips, etat.fourns, etat.docs]);

  // ── Sens 2 : l'adresse change, l'application suit ─────────────────────────
  //
  // `popstate` couvre le bouton « Précédent », le bouton « Suivant », le geste
  // de retour du pavé tactile et Alt+←. Les quatre passaient jusqu'ici à
  // travers l'application sans rien faire.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const surPop = (e: PopStateEvent) => {
      posePar = 'url';
      useGed.getState().appliquerAdresse(window.location.pathname);
      /* Et l'on REPOSE le défilement relevé quand on a quitté cette entrée.
         La restauration elle-même est différée par le magasin
         (`scrollARestaurer`) : au moment où l'on est ici, l'écran demandé n'est
         pas encore rendu, et défiler vers 3 000 px sur une page qui n'en fait
         pas encore 500 ne mène nulle part. */
      const etat = (e.state || {}) as { scrollY?: number; scrollsInternes?: unknown };
      useGed.setState({
        scrollARestaurer: typeof etat.scrollY === 'number' ? etat.scrollY : null,
        scrollsInternesARestaurer: Array.isArray(etat.scrollsInternes)
          ? (etat.scrollsInternes as { cle: string; top: number }[])
          : [],
      });
    };
    window.addEventListener('popstate', surPop);
    return () => window.removeEventListener('popstate', surPop);
  }, []);

  // ── Ouverture par LIEN : résolution de la référence ───────────────────────
  //
  // Elle ne peut pas se faire au premier rendu : les registres arrivent du
  // serveur. Cet effet réessaie à chaque arrivée de données, et ne conclut
  // « introuvable » que lorsque le registre concerné est effectivement chargé.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const attente = etat.lienEnAttente;
    if (!attente) return;
    const a = ADRESSES[attente.vue];
    if (!a?.cle || !a.registre) { useGed.setState({ lienEnAttente: null }); return; }
    const reg = registreDe(a.registre, etat as unknown as Ctx);
    const r = resoudre(attente.reference, reg, a.objet || 'cette fiche');
    if (r.etat === 'attente') return;
    if (r.etat === 'introuvable') {
      useGed.setState({ lienEnAttente: null, erreurLien: r.message });
      return;
    }
    useGed.setState({ lienEnAttente: null, erreurLien: null, [a.cle]: r.valeur, view: attente.vue });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [etat.lienEnAttente, etat.ncs, etat.actions, etat.recls, etat.audits, etat.rdds,
    etat.equips, etat.fourns, etat.docs]);

  // ── Le TITRE DE L'ONGLET suit l'écran ─────────────────────────────────────
  //
  // Posé ICI et non dans le Shell : c'est le même couple (écran, référence) que
  // celui écrit dans l'adresse, calculé par la même fonction. Séparés, le titre
  // et l'URL auraient fini par désigner deux choses différentes — l'onglet
  // annonçant une fiche que la barre d'adresse ne montrait plus.
  //
  // `document.title` en direct, et non `<title>` de Next : le module est une
  // application mono-page dont l'écran vit dans le store, jamais dans le
  // routeur. Les métadonnées du layout ne sont évaluées qu'une fois.
  useEffect(() => {
    if (typeof document === 'undefined' || !lue.current) return;
    const s = useGed.getState() as unknown as Ctx;
    const vue = s.view as GedView;
    document.title = titreDeLaPage(vue, referenceOuverte(vue, s));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [etat.view, etat.selectedNC, etat.selectedAction, etat.selectedRecl, etat.selectedAudit,
    etat.selectedRdd, etat.selectedEquip, etat.selectedFourn, etat.selectedCode,
    etat.selectedPoste, etat.selectedPersonne, etat.selectedRef,
    etat.ncs, etat.actions, etat.recls, etat.audits, etat.rdds, etat.equips, etat.fourns, etat.docs]);

  /* ── UN SECOND « première lecture de l'adresse » VIVAIT ICI ───────────────
     Retiré le 27/08/2026 (relevé MAJ-11).

     C'était un DOUBLON exact du tout premier effet de ce hook, et il annulait
     le message d'adresse inconnue à chaque arrivée à froid. L'enchaînement :

       1. le premier effet lit « /cqe », ne le reconnaît pas, pose
          `erreurLien` et retombe sur le tableau de bord ;
       2. la synchronisation, voyant `view: 'dash'`, réécrit l'adresse en « / » —
          ce qui est correct, l'écran affiché EST le tableau de bord ;
       3. ce second effet relisait alors « / », qui est une adresse
          parfaitement valide : il repassait par le chemin nominal, lequel
          remet `erreurLien: null`.

     Résultat mesuré : `/cqe` menait à l'accueil, adresse réécrite, **sans un
     mot** — le message existait, était posé, et effacé un tour de boucle plus
     tard. Rien à l'écran, rien en console : le genre de défaut qu'on ne trouve
     qu'en relisant l'ordre des effets.

     ⚠️ NE PAS LE REMETTRE « pour être sûr que l'adresse est lue ». Elle l'est,
     par le premier effet, qui doit rester DÉCLARÉ EN PREMIER (voir son propre
     commentaire) ; ajouter une seconde lecture après la synchronisation, c'est
     mécaniquement relire une adresse qu'on vient soi-même de réécrire. */
}

/** Marque que l'état qui suit vient de l'URL (utilisé par le store). */
export function marquerVenantDeLUrl() { posePar = 'url'; }

export { lireChemin };

/* ═══════════════════════════════════════════════════════════════════════════
 * L'ONGLET COURANT VIT DANS L'ADRESSE (relevé du 27/08/2026, P0.8).
 *
 * ── Ce qu'il répare ────────────────────────────────────────────────────────
 *
 * Les écrans à onglets — maintenances, fiche d'un examen, dossiers RH — les
 * portaient en état LOCAL. Trois conséquences :
 *
 *   · on ne pouvait pas envoyer « la vue Paramétrage » à quelqu'un : le lien
 *     ouvrait toujours le premier onglet ;
 *   · « Précédent » quittait la page au lieu de revenir à l'onglet précédent ;
 *   · F5 ramenait au premier onglet, au milieu d'un travail.
 *
 * ── Pourquoi la CHAÎNE DE REQUÊTE et non un segment de route ───────────────
 *
 * `?onglet=parametrage` s'ajoute à une adresse existante sans toucher aux
 * slugs. Or un slug publié est un CONTRAT : il vit dans des courriels et des
 * favoris. Ajouter un segment aurait voulu dire en inventer un par écran, et
 * en figer autant.
 *
 * ── Ce que le pont d'adresse fait, et ne fait pas ──────────────────────────
 *
 * ⚠️ La synchronisation d'écran (`useAdresse`, sens 1) compare le chemin SANS
 * la requête et sort quand il est déjà bon : changer d'onglet ne la réveille
 * donc pas, et elle n'efface pas le paramètre. En revanche, CHANGER D'ÉCRAN
 * écrit un chemin nu — ce qui est juste : l'onglet d'un écran n'a aucun sens
 * sur le suivant.
 * ═══════════════════════════════════════════════════════════════════════════ */
export function useOngletAdresse<T extends string>(defaut: T, permis: readonly T[]): [T, (v: T) => void] {
  const [valeur, poser] = useState<T>(() => {
    if (typeof window === 'undefined') return defaut;
    const v = new URLSearchParams(window.location.search).get('onglet') as T | null;
    return v && permis.includes(v) ? v : defaut;
  });

  // « Précédent » revient à l'onglet précédent : on lit l'adresse à chaque
  // `popstate`, comme le reste du pont.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const surPop = () => {
      const v = new URLSearchParams(window.location.search).get('onglet') as T | null;
      poser(v && permis.includes(v) ? v : defaut);
    };
    window.addEventListener('popstate', surPop);
    return () => window.removeEventListener('popstate', surPop);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [defaut]);

  const changer = useCallback((v: T) => {
    poser(v);
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    /* L'onglet PAR DÉFAUT ne s'écrit pas : une adresse partagée doit être la
       plus courte qui dise la même chose, et `?onglet=` sur le premier onglet
       n'apprend rien à personne. */
    if (v === defaut) url.searchParams.delete('onglet');
    else url.searchParams.set('onglet', v);
    // `pushState` et non `replaceState` : le relevé demande que « Précédent »
    // revienne à l'onglet précédent, ce qui suppose une entrée par onglet.
    window.history.pushState({ ...(window.history.state || {}) }, '', url.pathname + url.search);
  }, [defaut]);

  return [valeur, changer];
}
